import React, { useEffect } from 'react';
import WebFont from 'webfontloader';
import { useDispatch } from 'react-redux';
import { useLocation, Routes, Route } from 'react-router-dom';

import { loadUser } from './actions/userAction';
import Header from './components/Layouts/Header/Header';
import Footer from './components/Layouts/Footer/Footer';
import ProtectedRoute from './Routes/ProtectedRoute';

import Home from './components/Home/Home';
import Login from './components/User/Login';
import Register from './components/User/Register';
import Account from './components/User/Account';
import UpdateProfile from './components/User/UpdateProfile';
import UpdatePassword from './components/User/UpdatePassword';
import ForgotPassword from './components/User/ForgotPassword';
import ResetPassword from './components/User/ResetPassword';

import Products from './components/Products/Products';
import ProductDetails from './components/ProductDetails/ProductDetails';

import Cart from './components/Cart/Cart';
import Shipping from './components/Cart/Shipping';
import OrderConfirm from './components/Cart/OrderConfirm';
import Payment from './components/Cart/Payment';
import OrderSuccess from './components/Cart/OrderSuccess';
import OrderStatus from './components/Cart/OrderStatus';

import MyOrders from './components/Order/MyOrders';
import OrderDetails from './components/Order/OrderDetails';

import Wishlist from './components/Wishlist/Wishlist';
import NotFound from './components/NotFound';

import Dashboard from './components/Admin/Dashboard';
import OrderTable from './components/Admin/OrderTable';
import UpdateOrder from './components/Admin/UpdateOrder';
import ProductTable from './components/Admin/ProductTable';
import NewProduct from './components/Admin/NewProduct';
import UpdateProduct from './components/Admin/UpdateProduct';
import UserTable from './components/Admin/UserTable';
import UpdateUser from './components/Admin/UpdateUser';
import ReviewsTable from './components/Admin/ReviewsTable';

import AboutPage from './components/Pages/AboutPage';
import CareerPage from './components/Pages/CareerPage';
import ContactPage from './components/Pages/ContactPage';
import ConditionsPage from './components/Pages/ConditionsPage';
import PolocyPage from './components/Pages/PolocyPage';
import PaymentFailure from './components/Pages/PaymentFailure';
import PaymentSuccess from './components/Pages/PaymentSuccess';

function App() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  // Load Google fonts and user data on initial render
  useEffect(() => {
    WebFont.load({
      google: { families: ['Roboto:300,400,500,600,700'] },
    });
    dispatch(loadUser());
  }, [dispatch]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname]);

  // Define routes
  const routes = [
    { path: '/', element: <Home /> },
    { path: '/login', element: <Login /> },
    { path: '/register', element: <Register /> },
    { path: '/products', element: <Products /> },
    { path: '/products/:keyword', element: <Products /> },
    { path: '/product/:id', element: <ProductDetails /> },
    { path: '/cart', element: <Cart /> },
    { path: '/about', element: <AboutPage /> },
    { path: '/careers', element: <CareerPage /> },
    { path: '/contact', element: <ContactPage /> },
    { path: '/term&conditions', element: <ConditionsPage /> },
    { path: '/returnpolicy', element: <PolocyPage /> },
    { path: '/payment-failure', element: <PaymentFailure /> },
    { path: '/payment-success', element: <PaymentSuccess /> },

    // Protected routes
    { path: '/account', element: <ProtectedRoute><Account /></ProtectedRoute> },
    { path: '/account/update', element: <ProtectedRoute><UpdateProfile /></ProtectedRoute> },
    { path: '/password/update', element: <ProtectedRoute><UpdatePassword /></ProtectedRoute> },
    { path: '/password/forgot', element: <ForgotPassword /> },
    { path: '/password/reset/:token', element: <ResetPassword /> },
    { path: '/wishlist', element: <ProtectedRoute><Wishlist /></ProtectedRoute> },

    // Order process
    { path: '/shipping', element: <ProtectedRoute><Shipping /></ProtectedRoute> },
    { path: '/order/confirm', element: <ProtectedRoute><OrderConfirm /></ProtectedRoute> },
    { path: '/process/payment', element: <ProtectedRoute><Payment /></ProtectedRoute> },
    { path: '/orders/success', element: <OrderSuccess success={true} /> },
    { path: '/orders/failed', element: <OrderSuccess success={false} /> },
    { path: '/orders', element: <ProtectedRoute><MyOrders /></ProtectedRoute> },
    { path: '/order/:id', element: <ProtectedRoute><OrderStatus /></ProtectedRoute> },
    { path: '/order_details/:id', element: <ProtectedRoute><OrderDetails /></ProtectedRoute> },

    // Admin routes
    { path: '/admin/dashboard', element: <ProtectedRoute isAdmin={true}><Dashboard activeTab={0}><OrderTable /></Dashboard></ProtectedRoute> },
    { path: '/admin/orders', element: <ProtectedRoute isAdmin={true}><Dashboard activeTab={1}><OrderTable /></Dashboard></ProtectedRoute> },
    { path: '/admin/order/:id', element: <ProtectedRoute isAdmin={true}><Dashboard activeTab={1}><UpdateOrder /></Dashboard></ProtectedRoute> },
    { path: '/admin/products', element: <ProtectedRoute isAdmin={true}><Dashboard activeTab={2}><ProductTable /></Dashboard></ProtectedRoute> },
    { path: '/admin/new_product', element: <ProtectedRoute isAdmin={true}><Dashboard activeTab={3}><NewProduct /></Dashboard></ProtectedRoute> },
    { path: '/admin/product/:id', element: <ProtectedRoute isAdmin={true}><Dashboard activeTab={2}><UpdateProduct /></Dashboard></ProtectedRoute> },
    { path: '/admin/users', element: <ProtectedRoute isAdmin={true}><Dashboard activeTab={4}><UserTable /></Dashboard></ProtectedRoute> },
    { path: '/admin/user/:id', element: <ProtectedRoute isAdmin={true}><Dashboard activeTab={4}><UpdateUser /></Dashboard></ProtectedRoute> },
    { path: '/admin/reviews', element: <ProtectedRoute isAdmin={true}><Dashboard activeTab={5}><ReviewsTable /></Dashboard></ProtectedRoute> },

    // Fallback route
    { path: '*', element: <NotFound /> },
  ];

  return (
    <>
      <Header />
      <Routes>
        {routes.map(({ path, element }, index) => (
          <Route key={index} path={path} element={element} />
        ))}
      </Routes>
      <Footer />
    </>
  );
}

export default App;
